import {
  FeatureType,
  RecentPageType,
  type StandardPageRecentItem,
} from '__generated__';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { useKanso } from '@sbu/contexts.kanso';
import { DynamicFeatureFlag, useFeatureFlag } from '@sbu/hooks.featureflag';
import { useRecents } from '@sbu/hooks.recents';
import {
  UpdateStandardPageRecents,
  updateStandardPageRecents,
  UpdateStandardPageRecentsParams,
  FeatureType as UserSettingsFeatureType,
  RecentPageType as UserSettingsRecentPageType,
  StandardPageType as UserSettingsStandardPageType,
} from '@sbu/services.usersettings';

export type UseCSIEMRecentsConfig = Pick<
  StandardPageRecentItem,
  'accessId' | 'name' | 'standardPageType'
> | null;

export const useCSIEMRecents = (config: UseCSIEMRecentsConfig) => {
  const { addStandardPageRecentItems } = useRecents({
    // We want to avoid calling the initial query for three reasons:
    // 1. We don't read the recents in this hook
    // 2. We want to avoid conflicts with the initial query when triggering a refetch after a mutation
    // 3. Avoid side-effects when Kanso is disabled
    skipInitialRecentsQuery: true,
  });
  const CSIEMRecentsEnabled = useFeatureFlag(
    DynamicFeatureFlag.KansoRecentsGACSIEM
  );
  const deprecateNodeGraphQLUsage = useFeatureFlag(
    DynamicFeatureFlag.DeprecateNodeGraphQLUsage
  );
  const { navigationEnabled: kansoNavigationEnabled } = useKanso();

  const { accessId, name, standardPageType } = config ?? {};

  const { mutateAsync: updateRecents } = useMutation<
    UpdateStandardPageRecents | undefined,
    Error,
    UpdateStandardPageRecentsParams
  >({
    mutationKey: ['updateStandardPageRecents', config?.accessId],
    mutationFn: async (params) => {
      return await updateStandardPageRecents(params);
    },
  });

  const addRecentItem = useCallback(() => {
    if (
      kansoNavigationEnabled &&
      CSIEMRecentsEnabled &&
      accessId &&
      name &&
      standardPageType
    ) {
      const updatedPageData = {
        accessId,
        accessTime: Date.now(),
        id: Math.floor(Math.random() * 100000),
        name,
      };

      if (deprecateNodeGraphQLUsage) {
        updateRecents({
          ...updatedPageData,
          pageType: UserSettingsRecentPageType.StandardPage,
          standardPageType: standardPageType as UserSettingsStandardPageType,
          feature: UserSettingsFeatureType.Csiem,
        });
      } else {
        addStandardPageRecentItems([
          {
            __typename: 'StandardPageRecentItem',
            ...updatedPageData,
            pageType: RecentPageType.StandardPage,
            standardPageType,
            feature: FeatureType.Csiem,
          },
        ]);
      }
    }
  }, [
    kansoNavigationEnabled,
    CSIEMRecentsEnabled,
    accessId,
    name,
    standardPageType,
    deprecateNodeGraphQLUsage,
    addStandardPageRecentItems,
    updateRecents,
  ]);

  const addRecentOnDocumentVisible = useCallback(() => {
    if (document.visibilityState === 'visible') {
      addRecentItem();
    }
  }, [addRecentItem]);

  useEffect(() => {
    addRecentItem();
  }, [addRecentItem]);

  useEffect(() => {
    if (kansoNavigationEnabled && CSIEMRecentsEnabled) {
      document.addEventListener('visibilitychange', addRecentOnDocumentVisible);

      return () => {
        document.removeEventListener(
          'visibilitychange',
          addRecentOnDocumentVisible
        );
      };
    }
  }, [kansoNavigationEnabled, CSIEMRecentsEnabled, addRecentOnDocumentVisible]);
};
